import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import { CustomeTextField } from "../core/CustomeTextField";
import { uploadFetch } from "../../hooks/useFetch";
import { ISmtpProfile, ISmtpProfileClient } from "../../interfaces/ISmtpProfile";

export interface IProps {
    currentProfile: ISmtpProfile;
    setCurrentProfile: Function;
    /** Wird nach der Übernahme von Instanzen mit dem neuen Stand gerufen. */
    onProfileChanged: (profile: ISmtpProfile) => void;
}

export const getEmptySmtpProfile = () => {
    return {
        id: -1,
        name: "",
        email: "",
        server: "",
        port: "587",
        password: "",
        has_password: false,
        tls: true,
        note: "",
        client_count: 0,
        adoptable_count: 0,
        client_list: [],
        adoptable_list: [],
        created_at: "",
        updated_at: "",
    } as ISmtpProfile;
};

const ClientTable: React.FC<{ clientList: ISmtpProfileClient[] }> = ({ clientList }) => (
    <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell>Nr.</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell>Vertrag</TableCell>
                <TableCell>FQDN</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {clientList.map(x =>
                <TableRow key={`smtpprofile-client-${x.id}`}>
                    <TableCell>{x.id}</TableCell>
                    <TableCell>{x.company_name}{x.is_testing && <i> (Testing)</i>}</TableCell>
                    <TableCell>{x.contract_title}</TableCell>
                    <TableCell>{x.servername_fqdn}</TableCell>
                </TableRow>
            )}
        </TableBody>
    </Table>
);

interface ITestResult {
    ok: boolean;
    message: string;
    hints: string[];
}

export const SmtpProfileEdit: React.FC<IProps> = ({ currentProfile, setCurrentProfile, onProfileChanged }) => {
    const [isOpenAdopt, setIsOpenAdopt] = useState(false);
    const [isLoadingAdopt, setIsLoadingAdopt] = useState(false);
    const isNew = currentProfile.id < 0;
    //
    const [testReceiver, setTestReceiver] = useState("");
    const [testResult, setTestResult] = useState<ITestResult | null>(null);
    const [isTesting, setIsTesting] = useState(false);
    const canTest = currentProfile.server !== "" && currentProfile.port !== "" && currentProfile.email !== ""
        && (!isNew || (currentProfile.password ?? "") !== "");

    // Ein Ergebnis gilt nur für die getesteten Werte.
    useEffect(() => {
        setTestResult(null);
    }, [currentProfile.server, currentProfile.port, currentProfile.email, currentProfile.tls, currentProfile.password]);

    const handleTest = () => {
        setTestResult(null);
        uploadFetch("/smtpprofile/test", true, { ...currentProfile, receiver: testReceiver },
            setTestResult,
            (wasSuccessfully: boolean) => !wasSuccessfully && setTestResult({
                ok: false, message: "Der Test konnte nicht ausgeführt werden.", hints: [],
            }),
            undefined, setIsTesting);
    };

    const handleAfterAdopt = (res: ISmtpProfile) => {
        // Nur die Zähler übernehmen, damit ungespeicherte Eingaben erhalten bleiben.
        setCurrentProfile({
            ...currentProfile,
            client_count: res.client_count,
            adoptable_count: res.adoptable_count,
            client_list: res.client_list,
            adoptable_list: res.adoptable_list,
        });
        onProfileChanged(res);
        setIsOpenAdopt(false);
    };

    const handleAdopt = () => {
        uploadFetch(`/smtpprofile/${currentProfile.id}/adopt`, true, {}, handleAfterAdopt,
            undefined, undefined, setIsLoadingAdopt);
    };

    return (
        <>
            <Dialog open={isOpenAdopt} onClose={() => setIsOpenAdopt(false)}>
                <DialogTitle>Instanzen übernehmen</DialogTitle>
                <DialogContent>
                    {currentProfile.adoptable_count} DiNo-Instanz(en) mit eigenen Zugangsdaten
                    für <b>{currentProfile.email}</b> werden auf dieses Profil umgestellt.
                    Die Instanzen erhalten die Werte bei ihrer nächsten Lizenzaktualisierung
                    (spätestens nach zwei Tagen).
                    <ClientTable clientList={currentProfile.adoptable_list} />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setIsOpenAdopt(false)}>Abbruch</Button>
                    <Button variant="contained" onClick={handleAdopt} disabled={isLoadingAdopt}>
                        Umstellen
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={2} sx={{ mt: 1 }}>
                {!isNew && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            {currentProfile.client_count} DiNo-Instanz(en) nutzen dieses Profil.
                            Änderungen erreichen sie bei der nächsten Lizenzaktualisierung
                            (spätestens nach zwei Tagen). Hat ein Kunde in DiNo einen eigenen
                            Mailserver hinterlegt, bleibt dieser unberührt.
                        </Alert>
                    </Grid>
                )}

                {!isNew && currentProfile.adoptable_count > 0 && (
                    <Grid item xs={12}>
                        <Alert
                            severity="warning"
                            action={
                                <Button color="inherit" size="small" onClick={() => setIsOpenAdopt(true)}>
                                    Übernehmen
                                </Button>
                            }
                        >
                            {currentProfile.adoptable_count} weitere Instanz(en) haben {currentProfile.email} noch
                            einzeln hinterlegt.
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <CustomeTextField
                        label="Bezeichnung"
                        attr="name"
                        type="string"
                        object={currentProfile}
                        setObject={setCurrentProfile}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <CustomeTextField
                        label="Email"
                        attr="email"
                        type="email"
                        object={currentProfile}
                        setObject={setCurrentProfile}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        type="password"
                        label="Passwort"
                        fullWidth
                        size="small"
                        autoComplete="new-password"
                        value={currentProfile.password ?? ""}
                        onChange={(e) => setCurrentProfile({ ...currentProfile, password: e.target.value })}
                        required={isNew}
                        helperText={(!isNew && currentProfile.has_password) ? "Leer lassen, um das Passwort beizubehalten." : undefined}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <CustomeTextField
                        label="SMTP-Server"
                        attr="server"
                        type="string"
                        object={currentProfile}
                        setObject={setCurrentProfile}
                        required
                    />
                </Grid>
                <Grid item xs={6} sm={4}>
                    <CustomeTextField
                        label="SMTP-Port"
                        attr="port"
                        type="string"
                        object={currentProfile}
                        setObject={setCurrentProfile}
                        required
                    />
                </Grid>
                <Grid item xs={6} sm={2}>
                    <CustomeTextField
                        label="TLS"
                        attr="tls"
                        type="boolean"
                        object={currentProfile}
                        setObject={setCurrentProfile}
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2">Verbindung testen</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        type="email"
                        label="Testmail an (optional)"
                        fullWidth
                        size="small"
                        value={testReceiver}
                        onChange={(e) => setTestReceiver(e.target.value)}
                        helperText="Ohne Adresse werden nur Verbindung und Anmeldung geprüft. Es gelten die Werte im Formular, auch ungespeichert."
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Button variant="outlined" fullWidth onClick={handleTest} disabled={isTesting || !canTest}>
                        {isTesting ? "Teste …" : "Verbindung testen"}
                    </Button>
                </Grid>
                {testResult !== null && (
                    <Grid item xs={12}>
                        <Alert severity={!testResult.ok ? "error" : testResult.hints.length > 0 ? "warning" : "success"}>
                            {testResult.message}
                            {testResult.hints.map((hint, i) => <div key={`smtp-test-hint-${i}`}>{hint}</div>)}
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <CustomeTextField
                        label="Notiz"
                        attr="note"
                        type="string"
                        object={currentProfile}
                        setObject={setCurrentProfile}
                        rows={3}
                    />
                </Grid>

                {!isNew && (
                    <Grid item xs={12}>
                        <Typography variant="subtitle2">Genutzt von ({currentProfile.client_count})</Typography>
                        {currentProfile.client_list.length === 0
                            ? <Typography variant="body2"><i>Keine Instanz nutzt dieses Profil.</i></Typography>
                            : <ClientTable clientList={currentProfile.client_list} />
                        }
                    </Grid>
                )}
            </Grid>
        </>
    );
};
